import {
    ResultObj,
    allFetch,
} from "../Domain";
import { searchSettings } from "../types";

/**
 * @description 打卡地點查詢參數
 * @param {string} place_id 流水號
 * @param {string} name 名稱
 * @param {string} visible 是否可見
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface WorkClockPlaceSearchParams {
    place_id: string;
    name: string;
    visible: string;
    page: number;
    num_per_page: number;
}

export interface WorkClockPlaceSearchRestItem {
    place_id: string,
    name: string,
    ips: string,
    distance: string,
    longitude: string,
    latitude: string,
    visible: string
}

/**
 * @description 打卡地點查詢
 * 
 */
export const WorkClockPlaceSearch = async (
    obj: WorkClockPlaceSearchParams
): Promise<ResultObj<WorkClockPlaceSearchRestItem[]>> => {
    return await allFetch("UserWork/WorkClockPlaceSearch", obj);
};

/**
 * @description 打卡紀錄查詢參數
 * @param {string} place_id 打卡地點流水號
 * @param {string} type 種類(上班、下班)
 * @param {string} date_start 日期起
 * @param {string} date_end 日期迄
 * @param {string} isvalid 是否有效
 * @param {string} is_makeup 是否補打卡
 * @param {string[]} user_ids 帳號編號們
 * @param {string} company_id 公司流水號
 * @param {string} general_manager_id 高階管理階層（前總經理室）流水號
 * @param {string} department_id 部門流水號
 * @param {string} position_id 據點流水號
 * @param {string} class_id 課級流水號
 * @param {string} group_id 組級流水號
 * @param {string} office_id 辦公室流水號
 * @param {string} excel 是否匯出
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface WorkClockRecordSearchParams {
    place_id: string;
    type: string;
    date_start: string;
    date_end: string;
    isvalid: string;
    is_makeup: string;
    [searchSettings.user_ids]: string[];
    [searchSettings.company_id]: string;
    [searchSettings.general_manager_id]: string;
    [searchSettings.department_id]: string;
    [searchSettings.position_id]: string;
    [searchSettings.class_id]: string;
    [searchSettings.group_id]: string;
    [searchSettings.office_id]: string;
    excel: string;
    page: number;
    num_per_page: number;
}

/**
 * @description 打卡地點回傳
 * @param {string} name 名稱
 * @param {string} distance 距離
 * @param {string} longitude 經度
 * @param {string} latitude 緯度
 * @param {string[]} ips ip們限制
 * @param {string} visible 是否顯示
 * @param {string} place_id id
 */
export interface WorkClockRecordSearchResItem {
    cre_name: string;
    cre_time: string;
    date: string;
    distance: string;
    longitude: string;
    latitude: string;
    invalid_reason: string;
    ips: string;
    is_makeup: string;
    isvalid: string;
    makeup_reason_name: string;
    name: string;
    note: string;
    place_id: string;
    place_name: string;
    sc_code: string;
    status: string;
    time: string;
    type: string;
    user_id: string;
    username: string;
    visible: string,
}

/**
 * @description 打卡紀錄查詢
 */
export const WorkClockRecordSearch = async (
    obj: WorkClockRecordSearchParams
): Promise<ResultObj<WorkClockRecordSearchResItem[]>> => {
    return await allFetch("UserWork/WorkClockRecordSearch", obj);
};

/**
 * @description 打卡地點新建參數
 * @param {string} name 名稱
 * @param {string} distance 距離
 * @param {string} longitude 經度
 * @param {string} latitude 緯度
 * @param {string[]} ips ip們限制
 */
export interface WorkClockPlaceCreateParams {
    name: string;
    distance: string;
    longitude: string;
    latitude: string;
    ips: string[];
}

/**
 * @description 打卡地點新建
 */
export const WorkClockPlaceCreate = async (
    obj: WorkClockPlaceCreateParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkClockPlaceCreate", obj);
};


/**
 * @description 打卡地點刪除參數
 * @param {string} place_id 流水號
 */
export interface WorkClockPlaceDeleteParams {
    place_id: string;
}

/**
 * @description 打卡地點刪除
 */
export const WorkClockPlaceDelete = async (
    obj: WorkClockPlaceDeleteParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkClockPlaceDelete", obj);
};

/**
 * @description 打卡地點修改
 * @param {string} place_id 流水
 * @param {string} name 名稱
 * @param {string} distance 距離
 * @param {string} longitude 經度
 * @param {string} latitude 緯度
 * @param {string} visible 是否可見
 */
export interface WorkClockPlaceUpdateParams {
    place_id: string;
    name: string;
    distance: string;
    longitude: string;
    latitude: string;
    visible: string;
}

/**
 * @description 打卡地點更新
 */
export const WorkClockPlaceUpdate = async (
    obj: WorkClockPlaceUpdateParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkClockPlaceUpdate", obj);
};

/**
 * @description 打卡地點IP限制修改參數
 * @param {string} place_id 流水號
 * @param {string[]} ips ip們限制
 */
export interface WorkClockPlaceUpdateIPParams {
    place_id: string;
    ips: string[];
}

/**
 * @description 打卡地點IP限制修改
 */
export const WorkClockPlaceUpdateIP = async (
    obj: WorkClockPlaceUpdateIPParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkClockPlaceUpdateIP", obj);
};

/**
 * @description 調班申請查詢
 * @param {string} workshift_record_id 調班紀錄編號
 * @param {string} date_start 日期起
 * @param {string} date_end 日期迄
 * @param {string[]} statuses 審核狀態們
 * @param {string[]} user_ids 帳號編號們
 * @param {string} company_id 公司流水號
 * @param {string} general_manager_id 高階管理階層（前總經理室）流水號
 * @param {string} department_id 部門流水號
 * @param {string} position_id 據點流水號
 * @param {string} class_id 課級流水號
 * @param {string} group_id 組級流水號
 * @param {string} office_id 辦公室流水號
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface WorkShiftRecordSearchParams {
    workshift_record_id: string;
    date_start: string;
    date_end: string;
    statuses: string[];
    [searchSettings.user_ids]: string[];
    [searchSettings.company_id]: string;
    [searchSettings.general_manager_id]: string;
    [searchSettings.department_id]: string;
    [searchSettings.position_id]: string;
    [searchSettings.class_id]: string;
    [searchSettings.group_id]: string;
    [searchSettings.office_id]: string;
    page: number;
    num_per_page: number;
}



export interface WorkShiftRecordSearchResItem {
    audit_level: string;
    cre_time: string;
    date1: string;
    date2: string;
    name: string;
    note: string;
    status: string;
    user_cre_time: string;
    user_id: string;
    workshift_record_id: string;
}

/**
 * @description 調班申請查詢
 */
export const WorkShiftRecordSearch = async (
    obj: WorkShiftRecordSearchParams
): Promise<ResultObj<WorkShiftRecordSearchResItem[]>> => {
    return await allFetch("UserWork/WorkShiftRecordSearch", obj);
};

/**
 * @description 調班申請建立
 * @param {string} user_id 帳號編號
 * @param {string} date1 日期1
 * @param {string} date2 日期2
 * @param {string} note 備註
 */
export interface WorkShiftRecordCreateParams {
    user_id: string;
    date1: string;
    date2: string;
    note: string;
}

/**
 * @description 調班申請建立
 */
export const WorkShiftRecordCreate = async (
    obj: WorkShiftRecordCreateParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkShiftRecordCreate", obj);
};

/**
 * @description 調班撤銷申請建立
 * @param {string} workshift_record_id 調班紀錄編號
 * @param {string} note 備註
 */
export interface WorkShiftRecordCancelCreateParams {
    workshift_record_id: string;
    note: string;
}

/**
 * @description 調班撤銷申請建立
 */
export const WorkShiftRecordCancelCreate = async (
    obj: WorkShiftRecordCancelCreateParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkShiftRecordCancelCreate", obj);
};

/**
 * @description 加班申請查詢
 * @param {string} workovertime_record_id 加班紀錄編號
 * @param {string} date_start 日期起
 * @param {string} date_end 日期迄
 * @param {string[]} statuses 審核狀態們
 * @param {string[]} user_ids 帳號編號們
 * @param {string} company_id 公司流水號
 * @param {string} general_manager_id 高階管理階層（前總經理室）流水號
 * @param {string} department_id 部門流水號
 * @param {string} position_id 據點流水號
 * @param {string} class_id 課級流水號
 * @param {string} group_id 組級流水號
 * @param {string} office_id 辦公室流水號
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface WorkOvertimeRecordSearchParams {
    workovertime_record_id: string;
    date_start: string;
    date_end: string;
    statuses: string[];
    [searchSettings.user_ids]: string[];
    [searchSettings.company_id]: string;
    [searchSettings.general_manager_id]: string;
    [searchSettings.department_id]: string;
    [searchSettings.position_id]: string;
    [searchSettings.class_id]: string;
    [searchSettings.group_id]: string;
    [searchSettings.office_id]: string;
    page: number;
    num_per_page: number;
}
export interface WorkOvertimeRecordSearchResItem {
    audit_level: string;
    cre_time: string;
    date: string;
    name: string;
    note: string;
    overtime_work_minutes: number;
    rest_time_json: string;
    sc_id: string;
    sc_name: string;
    status: string;
    user_cre_time: string;
    user_id: string;
    work_time_json: string;
    workovertime_record_id: string;
}

/**
 * @description 加班申請查詢
 */
export const WorkOvertimeRecordSearch = async (
    obj: WorkOvertimeRecordSearchParams
): Promise<ResultObj<WorkOvertimeRecordSearchResItem[]>> => {
    return await allFetch("UserWork/WorkOvertimeRecordSearch", obj);
};

/**
 * @description 加班申請建立
 * @param {string} sc_id 班表流水號
 * @param {string} user_id 帳號編號
 * @param {string} date 日期
 * @param {string} WorkTime 工作時段物件
 * @param {any[]} RestTimeList 休息時段物件們
 * @param {string} note 備註
 */
export interface WorkOvertimeRecordCreateParams {
    sc_id: string;
    user_id: string;
    date: string;
    WorkTime: {
        work_across_night: string;
        work_time_end: string;
        work_time_start: string;
    },
    RestTimeList: {
        rest_across_night: string;
        rest_time_end: string;
        rest_time_start: string;
    }[],
    note: string;
}

/**
 * @description 加班申請建立
 */
export const WorkOvertimeRecordCreate = async (
    obj: WorkOvertimeRecordCreateParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkOvertimeRecordCreate", obj);
};

/**
 * @description 加班撤銷申請建立
 * @param {string} workovertime_record_id 加班紀錄編號
 * @param {string} note 備註
 */
export interface WorkOvertimeRecordCancelCreateParams {
    workovertime_record_id: string;
    note: string;
}

/**
 * @description 加班撤銷申請建立
 */
export const WorkOvertimeRecordCancelCreate = async (
    obj: WorkOvertimeRecordCancelCreateParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkOvertimeRecordCancelCreate", obj);
};

/**
 * @description 撤銷加班 申請審核
 * @param {string} workshift_record_id 加班紀錄編號
 * @param {string} status 審核狀態
 * @param {string} note 備註
 */
export interface WorkOvertimeRecordCancelAuditParams {
    workovertime_record_id: string;
    status: string;
    note: string;
}

/**
 * @description 撤銷加班 申請審核
 */
export const WorkOvertimeRecordCancelAudit = async (
    obj: WorkOvertimeRecordCancelAuditParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkOvertimeRecordCancelAudit", obj);
};

/**
 * @description 調班 申請審核
 * @param {string} workshift_record_id 調班紀錄編號
 * @param {string} status 審核狀態
 * @param {string} note 備註
 */
export interface WorkShiftRecordAuditParams {
    workshift_record_id: string;
    status: string;
    note: string;
}

/**
 * @description 調班 申請審核
 */
export const WorkShiftRecordAudit = async (
    obj: WorkShiftRecordAuditParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkShiftRecordAudit", obj);
};

/**
 * @description 撤銷調班 申請審核
 * @param {string} workshift_record_id 調班紀錄編號
 * @param {string} status 審核狀態
 * @param {string} note 備註
 */
export interface WorkShiftRecordCancelAuditParams {
    workshift_record_id: string;
    status: string;
    note: string;
}

/**
 * @description 撤銷調班 申請審核
 */
export const WorkShiftRecordCancelAudit = async (
    obj: WorkShiftRecordCancelAuditParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkShiftRecordCancelAudit", obj);
};

/**
 * @description 加班 申請審核
 * @param {string} workshift_record_id 加班紀錄編號
 * @param {string} status 審核狀態
 * @param {string} note 備註
 */
export interface WorkOvertimeRecordAuditParams {
    workovertime_record_id: string;
    status: string;
    note: string;
}

/**
 * @description 加班 申請審核
 */
export const WorkOvertimeRecordAudit = async (
    obj: WorkOvertimeRecordAuditParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkOvertimeRecordAudit", obj);
};

/**
 * @description 打卡
 * @param {string} user_id 帳號編號(不給預設拿token)
 * @param {string} longitude 經度
 * @param {string} latitude 緯度
 */
export interface WorkClockParams {
    user_id: string;
    longitude: string;
    latitude: string;
}

/**
 * @description 打卡
 */
export const WorkClock = async (
    obj: WorkClockParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkClock", obj);
};

/**
 * @description 補打卡
 * @param {string} user_id 帳號編號
 * @param {string} date 日期
 * @param {string} time 時間
 * @param {string} longitude 經度
 * @param {string} latitude 緯度
 * @param {string} makeup_reason 補打卡原因
 * @param {string} type 種類(上班、下班)
 * @param {string} note 備註
 */
export interface WorkMakeUpClockParams {
    user_id: string;
    date: string;
    time: string;
    longitude: string;
    latitude: string;
    makeup_reason: string;
    type: string;
    note: string;
}

/**
 * @description 補打卡
 */
export const WorkMakeUpClock = async (
    obj: WorkMakeUpClockParams
): Promise<ResultObj> => {
    return await allFetch("UserWork/WorkMakeUpClock", obj);
};
